/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { bindQuerySelector, el, hide, jsx, show } from '@amazon/vinyl-tsx'
import { getAuth, notSignedIn } from '@/auth/authRef'
import { logDebug } from '@amazon/vinyl-util'
import QRCode from 'qrcode'
import { ctx } from '@/util/context'
import { AuthStatus } from '@/auth/auth'

const { target, errorHandler } = ctx('CodeLinkCallout')

export function CodeLinkCallout() {
    return (
        <div
            id="codeLinkCallout"
            className="codeLinkCallout"
            onConnect={() => {
                const auth = getAuth()
                const signIn = () => {
                    // CBL Auth needs to call signIn to initialize the code.
                    displayPublicCodePrompt(null)
                    auth.signIn()
                }

                return auth.status.onData((value) => {
                    if (value === AuthStatus.SIGNED_OUT) {
                        setTimeout(signIn)
                    }
                })
            }}
            visible={notSignedIn()}
        >
            <div className="logo">
                <img src="/img/logo.svg" width={205} height={41} alt="logo" />
            </div>
            <h1>Start Listening Now</h1>
            <div className="qrCode"></div>
            <span className="codeLink" />
            <div className="instructions">
                Use your mobile phone to scan the QR code and then login on your mobile browser or
                enter this code at{' '}
                <a className="signInLink" target="_blank">
                    amazon.com/code
                </a>{' '}
                to login.
            </div>
        </div>
    )
}

/**
 * Updates the codeLinkCallout div with the public code.
 *
 * @param publicCode
 */
export function displayPublicCodePrompt(publicCode: string | null): () => void {
    logDebug(target, 'displayPublicCodePrompt', publicCode)
    const codeLinkCallout = el('#codeLinkCallout')
    const viewEl = bindQuerySelector(codeLinkCallout)
    viewEl('.codeLink').textContent = publicCode ?? '...'

    const signInLink = viewEl('.signInLink') as HTMLAnchorElement
    if (publicCode) {
        show(codeLinkCallout)
        const url = `https://www.amazon.com/code?cbl-code=${publicCode}&cbl-status=redirect_get_request`
        signInLink.href = url

        QRCode.toString(url, {
            type: 'svg',
            color: {
                dark: '#111111',
                light: '#ffffff',
            },
            margin: 2,
            width: 128,
        })
            .then((svg) => {
                viewEl('.qrCode').innerHTML = svg
            })
            .catch(errorHandler)
    } else {
        hide(codeLinkCallout)
        signInLink.href = '#'
    }
    return () => hide(codeLinkCallout)
}
